import type { ApiProblem } from "../../api/problem"

/**
 * Вход 1-Э1.1 — СЕМЬ СОСТОЯНИЙ.
 *
 * Здесь нет ни одного узла разметки, и это сделано ради проверок: «что человек видит
 * после отказа» — правило, а не вёрстка. Экран рисует то, что вернула эта функция.
 *
 * ГЛАВНОЕ ПРАВИЛО ЭКРАНА, КОТОРОЕ ЛЕГЧЕ ВСЕГО СЛОМАТЬ МОЛЧА: сервер намеренно отвечает
 * `invalid_credentials` и на «нет такого адреса», и на «не тот пароль», и на «учётка
 * заблокирована» (apps/api/src/modules/auth/auth.errors.ts). Различить их в интерфейсе
 * невозможно И НЕ НУЖНО: разный текст превратил бы форму входа в перечислитель
 * сотрудников салона. Поэтому «Не подошли» — это один текст на все три случая,
 * а «Отключена» приходит ДРУГИМ кодом (tenant_suspended), уже после того, как человек
 * опознан салоном.
 */

export type LoginStateKind =
  /** Обычная форма. */
  | "norm"
  /** «Номер или пароль не подошли». */
  | "bad"
  /** «Вход временно закрыт: слишком много попыток». */
  | "many"
  /** «Не получилось связаться с сервером». */
  | "off"
  /** Плановые работы: дело не в пароле. */
  | "maint"
  /** «Доступ к кабинету закрыт». */
  | "disabled"

/** Что случилось с формой к этому моменту. Больше состояний у неё нет. */
export type LoginOutcome =
  | { readonly kind: "idle" }
  | { readonly kind: "problem"; readonly problem: ApiProblem }

export interface LoginView {
  readonly kind: LoginStateKind
  /** Строка ошибки под полями. null — ошибки нет. */
  readonly errorText: string | null
  /** Плашка над полями: тёмная, про плановые работы. */
  readonly maintenanceText: string | null
  /** Есть ли поле пароля. Сейчас оно есть всегда — вход по ссылке в API не заведён. */
  readonly showPassword: boolean
  readonly submitLabel: string
  /** Кнопка видимо недоступна: нажимать бессмысленно, и это видно до нажатия. */
  readonly submitDimmed: boolean
  /** Рамка поля логина — тревожная. */
  readonly loginInvalid: boolean
}

/**
 * Сколько ждать после «слишком много попыток».
 *
 * Сервер отдаёт 429 rate_limited, но НЕ отдаёт ни Retry-After, ни поля в теле отказа,
 * — то есть настоящего остатка кабинет не знает. Пять минут взяты не с потолка: ровно
 * это обещает человеку сам текст отказа («Попробуйте через 5 минут»), и расходиться
 * с текстом, который он читает, нельзя. Как только сервер начнёт присылать остаток,
 * значение придёт сюда и умолчание уйдёт.
 */
export const RATE_LIMIT_WAIT_SECONDS = 5 * 60

const TEXT_BAD =
  "Номер или пароль не подошли. Проверьте номер и попробуйте ещё раз — или войдите по ссылке на почту."
const TEXT_MANY =
  "Вход временно закрыт: слишком много попыток. Попробуйте через 5 минут или войдите по ссылке на почту."
const TEXT_OFF =
  "Не получилось связаться с сервером. Проверьте интернет и повторите — введённое осталось в полях."
const TEXT_DISABLED =
  "Доступ к кабинету закрыт. Если это ошибка — обратитесь к владельцу салона."
const TEXT_MAINT_TAIL = " — идут плановые работы. Дело не в пароле."

/**
 * Вход по номеру телефона.
 *
 * Макет рисует первым полем «Телефон», а сервер принимает только почту
 * (CabinetLoginRequest.email). Пока это так, форма обязана сказать об этом ДО отправки:
 * молча отправить номер значит получить «не подошли» и отправить человека искать
 * несуществующую опечатку в правильном номере.
 */
export const TEXT_PHONE_NOT_SUPPORTED =
  "Вход по номеру телефона пока не работает — введите рабочую почту. Если почты нет, её знает владелец салона."

/** Чем человек представился. Разбор нарочно грубый: решается один вопрос — что отправлять. */
export function credentialKind(value: string): "email" | "phone" | "empty" {
  const trimmed = value.trim()
  if (trimmed.length === 0) return "empty"
  if (trimmed.includes("@")) return "email"
  // Номер набирают со слуха: «+7 (916) 123-45-67», «8 916…». Всё, что состоит только
  // из цифр и разделителей номера, — это попытка войти по телефону, а не кривая почта.
  return /^[+\d][\d\s()\-.]*$/.test(trimmed) ? "phone" : "email"
}

/** «4:58», «0:42». Секунды ниже нуля не бывают: отсчёт останавливается на нуле. */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(safe / 60)
  return `${minutes}:${String(safe % 60).padStart(2, "0")}`
}

/**
 * Плановые работы.
 *
 * Признака режима платформы (read_only / maintenance) API не отдаёт ни в /me, ни
 * заголовком, ни в /health/ready — это названный разрыв этапа. Единственное, чем
 * кабинет располагает, — 503 от самого сервиса. Время окончания работ приходит только
 * если сервер положил его в detail; выдумывать «до 04:30» нельзя — человек по этому
 * времени планирует смену.
 */
function maintenanceTextOf(problem: ApiProblem): string {
  const detail = problem.detail
  if (detail !== null && detail.trim().length > 0) return detail
  return `Кабинет недоступен${TEXT_MAINT_TAIL}`
}

/** Отказ → состояние. Здесь и только здесь коды сервера превращаются в экраны. */
export function loginKindOfProblem(problem: ApiProblem): LoginStateKind {
  if (problem.code === "offline" || problem.code === "timeout") return "off"
  if (problem.code === "rate_limited") return "many"
  if (
    problem.code === "tenant_suspended" ||
    problem.code === "tenant_forbidden" ||
    problem.code === "permission_denied"
  ) {
    return "disabled"
  }
  // 503 — это «сервис не принимает запросы», а не «пароль не тот». Единственный
  // доступный признак плановых работ, пока API не отдаёт режим платформы явно.
  if (problem.status === 503) return "maint"
  return "bad"
}

export interface LoginViewInput {
  readonly outcome: LoginOutcome
  /** Остаток запрета входа, секунды. Считает экран, начиная с RATE_LIMIT_WAIT_SECONDS. */
  readonly retryIn: number
}

export function loginView(input: LoginViewInput): LoginView {
  const base: LoginView = {
    kind: "norm",
    errorText: null,
    maintenanceText: null,
    showPassword: true,
    submitLabel: "Войти",
    submitDimmed: false,
    loginInvalid: false,
  }

  if (input.outcome.kind === "idle") return base

  const kind = loginKindOfProblem(input.outcome.problem)

  switch (kind) {
    case "bad":
      // Пароль стирается, номер остаётся: набирать заново надо ровно то, в чём ошибка
      // могла быть, а не всё подряд. Стирает его экран — здесь только признак.
      return { ...base, kind, errorText: TEXT_BAD, loginInvalid: true }
    case "many":
      return {
        ...base,
        kind,
        errorText: TEXT_MANY,
        submitLabel: `Попробовать через ${formatCountdown(input.retryIn)}`,
        submitDimmed: input.retryIn > 0,
      }
    case "off":
      // Введённое остаётся в полях — это обещано текстом отказа, и нарушать его нельзя.
      return { ...base, kind, errorText: TEXT_OFF }
    case "maint":
      return {
        ...base,
        kind,
        maintenanceText: maintenanceTextOf(input.outcome.problem),
        submitDimmed: true,
      }
    case "disabled":
      return { ...base, kind, errorText: TEXT_DISABLED, submitDimmed: true }
    default:
      return base
  }
}
